import { FC, MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { NotificationResource } from "@/types/resources";
import Notification from ".";
import useAlerts from "@/hooks/useAlerts";
import Button from "../Button";
import { api } from "@/utils/api";

interface FailedJobNotificationData {
  title: string;
  description: string;
}

interface Props {
  notification: NotificationResource<FailedJobNotificationData>;
  retriable?: boolean;
  linkPath?: string;
  linkText?: string;
  queryKey?: Array<string | object>;
  minimal?: boolean;
  onRowDelete?: () => Promise<any>;
}

const FailedJobNotification: FC<Props> = ({
  notification,
  retriable = false,
  linkPath,
  linkText,
  queryKey,
  minimal = false,
  onRowDelete,
}) => {
  const queryClient = useQueryClient();

  const {
    id,
    data: { title, description },
  } = notification;

  const alerts = useAlerts();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this row?"
      );

      if (!confirmed) return;

      await onRowDelete!();

      alerts.close();
    } catch (err) {
      alerts.error();
    }
  };

  const onRetry = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to retry this job?"
      );

      if (!confirmed) return;

      await api.post(`/v1/notifications/${id}/retry-job`);

      if (queryKey) {
        queryClient.invalidateQueries(queryKey, {
          exact: false,
        });
      }

      alerts.success("This job is pushed back to the queue");
    } catch (err) {
      alerts.error();
    }
  };

  const onIgnore = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to ignore this message?"
      );

      if (!confirmed) return;

      await api.put(`/v1/notifications/${id}/read`);

      if (queryKey) {
        queryClient.invalidateQueries(queryKey, {
          exact: false,
        });
      }

      alerts.success("Notification successfully marked as read");
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Notification
      asListItem
      heading={title}
      actions={
        minimal ? (
          <>
            {!!onRowDelete && (
              <Button small $type="secondary" onClick={onDelete}>
                Delete
              </Button>
            )}
          </>
        ) : (
          <>
            <Button small $type="secondary" onClick={onIgnore}>
              Ignore
            </Button>
            {retriable && (
              <Button small $type="primary" onClick={onRetry}>
                Retry
              </Button>
            )}
            {!!linkPath && (
              <Button small $type="primary" as={Link} to={linkPath}>
                {linkText ?? "Preview"}
              </Button>
            )}
          </>
        )
      }
    >
      <span dangerouslySetInnerHTML={{ __html: description }} />
    </Notification>
  );
};

export default FailedJobNotification;
